// ─────────────────────────────────────────────────────────────────────────────
// The locale registry — the one place that knows which languages exist.
//
// Adding a language is meant to be a data change, not a code change: append an
// entry here, drop a folder under `translations/`, register its loaders in
// loader.js, and every picker, formatter and direction switch picks it up. No
// component, screen or service is allowed to branch on a locale id — if a
// behaviour differs per language it belongs in the descriptor below.
//
// Ported from the clinic panel (`parsina.client.clinic/src/i18n`), whose
// `docs/I18N.md` and the doctor app's are the fuller treatment. The mechanics
// are unchanged on purpose: this is the fourth panel to need them, and a fourth
// private implementation of "which language is this" is how the three drift.
//
// TWO differences from the clinic's copy, both deliberate:
//
//   • No `ar`. The platform back office is an INTERNAL tool for Parsina staff,
//     not a customer surface — there is no Arabic-speaking audience for it. The
//     registry supports adding one later without touching a component; what it
//     would need is the four CLDR corrections the clinic's `ar` descriptor
//     documents (ICU's Arabic month names for the Jalali calendar are corrupt
//     in six of twelve), so copy that descriptor rather than writing a new one.
//
//   • `FALLBACK_LOCALE` is `fa`, not `en`. See the note on it below.
//
// THE BRAND (src/lib/brand.js, configured at boot by src/lib/brandBoot.js)
// narrows this list and moves its defaults, never the descriptors' data:
// `locales` decides which of these languages are OFFERED (`isOfferedLocale` —
// every resolver below asks it, so a language the brand does not offer is never
// started in or cached), and `defaultLocale` is the first-visit language.
// `calendar` swaps each descriptor's calendar through the descriptor's own
// `calendars` data. With no brand all three are exactly what they were. Same
// rules as the clinic panel's config.js.
// ─────────────────────────────────────────────────────────────────────────────

import { brandLocales, brandDefaultLocale, calendarSystem } from '../lib/brand';

// The twelve Jalali months as the English UI spells them, index 0 = Farvardin.
// French shows its Jalali months in these words (PM-0185, D8) rather than ICU's
// French data («ordibehešt», «khordâd»), which reads as scholarly
// transliteration and is spelled differently by different ICU builds — the
// kit's date picker and the server's text use the same twelve words. German
// does the same (PM-0212) instead of ICU's «Schahriwar», «Ordibehescht».
const JALALI_MONTHS_LATIN = [
  'Farvardin', 'Ordibehesht', 'Khordad', 'Tir', 'Mordad', 'Shahrivar',
  'Mehr', 'Aban', 'Azar', 'Dey', 'Bahman', 'Esfand',
];

/**
 * A locale descriptor.
 *
 *  id       BCP-47 tag. Also the folder name under `translations/`.
 *  label    The language's name IN THAT LANGUAGE. A picker must be readable by
 *           someone who cannot read the language currently on screen, which is
 *           exactly the person using it, so this is never translated.
 *  dir      Writing direction — drives <html dir> and every logical CSS property.
 *  intl     The tag handed to Intl.*. Carries the calendar and numbering system
 *           as Unicode extensions, so one string configures every formatter and
 *           no formatter ever branches on `id`.
 *  timeZone The platform runs on Iran local time regardless of UI language: a
 *           subscription that expires at midnight expires at midnight in Tehran,
 *           not in the operator's browser zone. An ops panel whose dates drift
 *           by a day depending on who is looking cannot be used to answer
 *           «آیا این کلینیک منقضی شده؟».
 *
 * Optional fields (`dateStyles`, `stripEra`, `numericDateOrder`, `monthNames`,
 * `clinicCalendarIntl`, `readsPersianData`) exist so a locale whose CLDR data or
 * whose reader differs can say so as DATA. They are the escape hatch that keeps
 * the rest of the app free of `if (locale === …)`. A locale that omits them gets
 * the default behaviour, which is `fa` and `en` here.
 *
 *  fallsBackTo Where a key MISSING from this locale is looked up before
 *             FALLBACK_LOCALE (`fallbackLocalesFor`). French and German read
 *             English before Persian: their operators can read the one, not
 *             the other.
 *  calendar   The calendar `intl` is on: 'jalali' or 'gregory'.
 *  calendars  What changes when a BRAND puts every language on one calendar
 *             (`calendar` in the brand): fields merged over this descriptor,
 *             keyed by that calendar. A field set to `undefined` is a correction
 *             that does not apply there — the era a Jalali date drags along means
 *             nothing on a Gregorian one.
 */
export const LOCALES = {
  // i18n-exempt: a locale's `label` is its ENDONYM — the name the language gives
  // itself — so it is deliberately not translated. «فارسی» stays «فارسی» in the
  // English UI, the same way «English» stays «English» in the Persian one; that
  // is what makes a language switcher usable to someone who cannot read the
  // language currently on screen. `weekdaySeparator` is likewise locale data.
  fa: {
    id: 'fa',
    label: 'فارسی',
    englishLabel: 'Persian',
    dir: 'rtl',
    // -u-ca-persian → Jalali (Shamsi) calendar; -u-nu-arabext → Persian digits.
    intl: 'fa-IR-u-ca-persian-nu-arabext',
    calendar: 'jalali',
    calendars: {
      gregory: { intl: 'fa-IR-u-ca-gregory-nu-arabext' },
    },
    timeZone: 'Asia/Tehran',
    weekdaySeparator: '، ',
    // Persian is the language the platform's own stored data is in — clinic
    // names, clinic types, the notes an operator types onto an activation.
    readsPersianData: true,
  },
  en: {
    id: 'en',
    label: 'English',
    englishLabel: 'English',
    dir: 'ltr',
    // JALALI, not Gregorian — and this is the one place this panel's registry
    // differs in SUBSTANCE from the clinic panel's rather than in scope.
    //
    // A subscription period, a trial deadline and a payment period are the same
    // commercial facts the clinic sees in its own panel and on its invoices, and
    // those are Jalali end to end. With `en-US` this screen said "Sep 18, 2026"
    // where the clinic's own screen said «۲۷ شهریور ۱۴۰۵» — so an operator on the
    // phone to a clinic was reading a different date than the person they were
    // helping, about the same row. Language changes words and direction; it must
    // not change what a date MEANS.
    //
    // ICU renders the Jalali months in Latin for this tag: «Shahrivar 27, 1405».
    // The numbering system is untouched, so digits stay Latin.
    intl: 'en-US-u-ca-persian',
    calendar: 'jalali',
    calendars: {
      // A brand on the Gregorian calendar (Proxia) keeps no Jalali books: plain
      // US English, and neither Jalali correction below applies.
      gregory: { intl: 'en-US', stripEra: undefined, numericDateOrder: undefined },
    },
    timeZone: 'Asia/Tehran',
    weekdaySeparator: ', ',
    // ICU appends the era — «Shahrivar 27, 1405 AP» — for a calendar that is not
    // the locale's own default, and `era: undefined` does not suppress it. Nobody
    // writing a Jalali date writes the era.
    stripEra: true,
    // ICU writes the all-numeric form US-style, month-first («6/27/1405»), where
    // Persian writes it year-first («۱۴۰۵/۰۶/۲۷»). Two locales disagreeing about
    // the field order of the same calendar on the same product is how a date gets
    // transcribed wrong. Applied only when the date really is all-numeric, so a
    // month NAME is never reordered.
    numericDateOrder: ['year', 'month', 'day'],
  },
  // French mirrors English (PM-0185, D3), which here means JALALI — for the
  // same reason: an operator on the phone to a clinic reads the date the clinic
  // reads. Latin digits, the era dropped, the numeric form year-first, exactly
  // as `en` does it; what differs is French data.
  fr: {
    id: 'fr',
    label: 'Français',
    englishLabel: 'French',
    dir: 'ltr',
    intl: 'fr-FR-u-ca-persian',
    calendar: 'jalali',
    calendars: {
      // A brand on the Gregorian calendar: plain French dates, and none of the
      // Jalali corrections — the month names below would rename August.
      gregory: {
        intl: 'fr-FR', stripEra: undefined, numericDateOrder: undefined, monthNames: undefined,
      },
    },
    timeZone: 'Asia/Tehran',
    // «samedi 27 Shahrivar 1405» — French joins the weekday without a comma.
    weekdaySeparator: ' ',
    // ICU's French `medium` abbreviates the month («27 šahr. 1405»); the panel's
    // default date carries the whole name, as Persian's does.
    dateStyles: {
      medium: { day: 'numeric', month: 'long', year: 'numeric' },
    },
    stripEra: true,
    numericDateOrder: ['year', 'month', 'day'],
    monthNames: JALALI_MONTHS_LATIN,
    fallsBackTo: 'en',
  },
  // German mirrors English too (PM-0212, as French D3): JALALI here, Latin
  // digits, the era dropped, the numeric form year-first, the months spelled as
  // the English UI spells them. What differs is German data — «27. Shahrivar
  // 1405», the weekday joined with a comma, «1.234» grouped with a dot.
  de: {
    id: 'de',
    label: 'Deutsch',
    englishLabel: 'German',
    dir: 'ltr',
    intl: 'de-DE-u-ca-persian',
    calendar: 'jalali',
    calendars: {
      // A brand on the Gregorian calendar: plain German dates, and none of the
      // Jalali corrections — the month names below would rename August.
      gregory: {
        intl: 'de-DE', stripEra: undefined, numericDateOrder: undefined, monthNames: undefined,
      },
    },
    timeZone: 'Asia/Tehran',
    // «Freitag, 27. Shahrivar 1405» — German joins the weekday with a comma.
    weekdaySeparator: ', ',
    dateStyles: {
      // ICU's German `short` has a TWO-digit year («27.06.05»), which the
      // year-first reorder below would turn into «05.06.27»; the four-digit
      // year keeps the numeric date unambiguous: «1405.06.27».
      short: { day: '2-digit', month: '2-digit', year: 'numeric' },
      // ICU's German `medium` is all-numeric («27.06.1405»); the panel's
      // default date carries the whole month name, as Persian's does.
      medium: { day: 'numeric', month: 'long', year: 'numeric' },
    },
    stripEra: true,
    numericDateOrder: ['year', 'month', 'day'],
    monthNames: JALALI_MONTHS_LATIN,
    fallsBackTo: 'en',
  },
};

export const LOCALE_IDS = Object.keys(LOCALES);

/** The product default: what the panel is, absent any signal. */
export const DEFAULT_LOCALE = 'fa';

/**
 * Where a MISSING KEY is looked up next, and the last resort when a stored tag
 * is not one we ship.
 *
 * `fa`, where the clinic and doctor panels use `en`. The difference is which
 * catalogue is the REFERENCE, and here it is Persian: this panel's copy is
 * authored in Persian (the operators are Parsina's own Iranian staff), and
 * English is the translation of it. Falling back to the source catalogue is the
 * point of the mechanism — a key missing from `en` degrades to correct Persian
 * rather than to a humanised key fragment.
 *
 * It is the LAST hop, not always the next one: a descriptor may name a nearer
 * one (`fallsBackTo`), and French and German do — a key missing from `fr` or
 * `de` reads English first, Persian only if English lacks it too
 * (`fallbackLocalesFor`).
 *
 * In practice it almost never fires: `i18nCoverage.test.js` fails the build when
 * a catalogue disagrees with the Persian one about a single key. It matters on the one day
 * someone adds a Persian string and forgets its translation, and on that day the
 * screen still reads correctly for the person most likely to be looking.
 */
export const FALLBACK_LOCALE = 'fa';

/**
 * Translation namespaces — one file per module, per locale.
 *
 * The first segment of every key IS the namespace: `t('clinics.columns.until')`
 * reads `translations/<locale>/clinics.json`. That makes a key self-locating —
 * you can find the string a screen shows without knowing anything about how the
 * loader works.
 *
 * These are the BACK OFFICE's modules. The panel has three screens and a shell,
 * so the list is short by design; a namespace per screen plus the four
 * cross-cutting ones is the whole product.
 */
export const NAMESPACES = [
  'common',    // actions, units, generic nouns — used everywhere
  'nav',       // sidebar, account menu, document titles, skip link
  'validation',// field-level validation messages
  'errors',    // BFF failures, error states, whole-page system states
  'auth',      // the sign-in gate: checking, denied, redirect loop, session end
  'shortcuts', // the keyboard-shortcut help overlay (`?`)
  'dashboard', // growth, revenue, KPIs, status and tier mix
  'clinics',   // the estate list: filters, columns, backfill
  'clinic',    // one clinic's record: subscription, periods, events, actions
  'aiPricing', // AI cost & Parseh pricing: overview, models, services, policies, ledger
  'marketplace', // «بازار»: vendor requests, listing review, settlements, commission
  'demoRequests', // «درخواست‌های دمو»: the CRM demo-request queue, drawer and dialogs
  'whatsapp',  // «واتس‌اپ پارسینا»: linking the company number, its state, a test send
  'doctorPages', // «صفحات پزشکان»: doctors' public pages — list, suspend, restore
  'countries', // «کشورها»: country profiles, their history, a clinic's country settings, insurers by country
];

export const isSupportedLocale = (id) => Object.prototype.hasOwnProperty.call(LOCALES, id);

/** The ids a person may choose: the shipped ones, narrowed to the brand's `locales`. */
export const offeredLocaleIds = () => brandLocales(LOCALE_IDS);

/** Shipped AND offered by the brand. Without a brand, the same as `isSupportedLocale`. */
export const isOfferedLocale = (id) => isSupportedLocale(id) && offeredLocaleIds().includes(id);

/**
 * The descriptors a language picker lists — `LOCALES` itself unless the brand
 * offers fewer.
 */
export function offeredLocales() {
  const ids = offeredLocaleIds();
  if (ids.length === LOCALE_IDS.length) return LOCALES;
  return Object.fromEntries(ids.map((id) => [id, LOCALES[id]]));
}

/**
 * The language for someone the panel knows nothing about: DEFAULT_LOCALE, or
 * the brand's `defaultLocale` (and never one the brand does not offer).
 */
export const productDefaultLocale = () => brandDefaultLocale(DEFAULT_LOCALE, LOCALE_IDS);

/**
 * Where a missing key is looked up next: FALLBACK_LOCALE while the brand offers
 * it, else the product default — a brand without Persian never shows a Persian
 * string, not even for a key its own catalogue lacks.
 */
export const fallbackLocale = () => (isOfferedLocale(FALLBACK_LOCALE) ? FALLBACK_LOCALE : productDefaultLocale());

/**
 * Every locale a key missing from `id` is looked up in, nearest first: the
 * descriptor's own `fallsBackTo` while the brand offers it, then
 * `fallbackLocale()`. Never `id` itself and never twice — `fa` has no hop, `en`
 * one (Persian), `fr` and `de` two (English, then Persian: PM-0185 D4, PM-0212 —
 * a French or German reader is shown English before Persian).
 */
export function fallbackLocalesFor(id) {
  const chain = [];
  const own = LOCALES[id]?.fallsBackTo;
  if (own && own !== id && isOfferedLocale(own)) chain.push(own);
  const last = fallbackLocale();
  if (last !== id && !chain.includes(last)) chain.push(last);
  return chain;
}

/**
 * The descriptor for a locale, or the product default's if the id is unknown —
 * on the brand's calendar when the brand names one (the descriptor's own
 * `calendars` data); otherwise the descriptor itself, untouched.
 */
export const localeDescriptor = (id) => {
  const descriptor = LOCALES[id] || LOCALES[productDefaultLocale()];
  const calendar = calendarSystem(null);
  if (!calendar || calendar === descriptor.calendar) return descriptor;
  return { ...descriptor, ...descriptor.calendars?.[calendar], calendar };
};

/**
 * Narrow a BCP-47 tag to a locale we ship and the brand offers: `en-GB` → `en`,
 * `fa-IR` → `fa`. Returns null for anything else, so callers can tell "no
 * match" from "matched the default".
 */
export function normalizeLocale(tag) {
  if (!tag) return null;
  const raw = String(tag).trim();
  const base = raw.toLowerCase().split(/[-_]/)[0];
  const id = isSupportedLocale(raw) ? raw : (isSupportedLocale(base) ? base : null);
  return id && isOfferedLocale(id) ? id : null;
}

// ── Persistence ───────────────────────────────────────────────────────────────
//
// localStorage is the WHOLE record here, not a cache — and that is a known
// limitation rather than a design choice. In the clinic and doctor panels the
// record is the signed-in user's `locale` attribute in Keycloak, so the choice
// follows a person to another workstation. This BFF has no preference mutation
// to write it with (see the redesign spec, open issue 3), so an operator's
// language is per-browser until one exists. Adding it later is additive: set
// `language` on the provider from `me` and the adoption effect already in
// I18nProvider takes over, exactly as it does in the clinic app.

export const STORAGE_KEY = 'parsina.locale';

export function readStoredLocale() {
  try {
    return normalizeLocale(window.localStorage.getItem(STORAGE_KEY));
  } catch {
    // Private mode, disabled storage, or no window at all (tests, SSR).
    return null;
  }
}

export function storeLocale(id) {
  try {
    if (isOfferedLocale(id)) window.localStorage.setItem(STORAGE_KEY, id);
  } catch {
    /* A locale we cannot cache still works — it just re-resolves next launch. */
  }
}

/**
 * The language to start in:
 *
 *   1. what the operator last chose
 *   2. DEFAULT_LOCALE
 *
 * There is deliberately no `navigator.language` step between the two, and the
 * reason is the same one the clinic panel records: Windows and Chrome in Iran
 * are overwhelmingly installed with an en-US locale, so `navigator.languages`
 * reports `['en-US']` for a Persian-speaking operator who has expressed no
 * preference at all. Device detection cannot tell that apart from a real choice
 * of English, so it reads a setup artifact as a preference and opens the panel
 * LTR and Gregorian for someone who wanted neither.
 *
 * Persian is therefore the floor, not a guess. A real preference still wins
 * outright via step 1, fed by the picker in the account menu — a stated choice,
 * not an inferred one.
 *
 * A brand moves step 2 to its own `defaultLocale` (`productDefaultLocale`), and
 * step 1 only ever answers a language the brand offers (`normalizeLocale`), so a
 * cached choice the brand does not offer falls through to step 2. index.html's
 * pre-paint script makes the same two moves; keep them in step.
 */
export function resolveInitialLocale() {
  return readStoredLocale() || productDefaultLocale();
}

/**
 * Push a locale onto the document: `<html lang dir>`.
 *
 * `dir` is what makes RTL work — every logical CSS property (margin-inline-*,
 * padding-inline-*, inset-inline-*, text-align: start/end) resolves against it,
 * so this single attribute mirrors the whole layout. Nothing else in the app may
 * ever set `direction` on a container; the shell used to set `dir="rtl"` on its
 * own root, which is what made English impossible.
 *
 * Returns the id actually applied, so callers can trust the result over their
 * input when the input was junk.
 */
export function applyDocumentLocale(id) {
  const locale = isOfferedLocale(id) ? id : productDefaultLocale();
  const root = typeof document !== 'undefined' ? document.documentElement : null;
  if (root) {
    root.setAttribute('lang', locale);
    root.setAttribute('dir', LOCALES[locale].dir);
  }
  return locale;
}
